import type { Dosage, Quantity } from '@medplum/fhirtypes'
import type { ExtendedMedicationRequest } from '#src/models/medicationRequest.ts'

const STRENGTH_UNITS = 'mcg|µg|ug|mg|g|ml|iu'
const STRENGTH_PATTERN = new RegExp(
  `(\\d+(?:\\.\\d+)?)\\s*(${STRENGTH_UNITS})\\b`,
  'i'
)
const STRUCTURED_STRENGTH_UNIT_PATTERN = /^(?:mcg|µg|ug|mg|iu)$/i

interface FormAliases {
  [alias: string]: string
}

const FORM_ALIASES: FormAliases = {
  tab: 'tablet',
  tabs: 'tablet',
  tablet: 'tablet',
  tablets: 'tablet',
  cap: 'capsule',
  caps: 'capsule',
  capsule: 'capsule',
  capsules: 'capsule',
  supp: 'supp',
  suppository: 'supp',
  suppositories: 'supp',
  patch: 'patch',
  patches: 'patch',
  puff: 'puff',
  puffs: 'puff',
  drop: 'drop',
  drops: 'drop',
}

const FORM_PATTERN = new RegExp(
  `\\b(${Object.keys(FORM_ALIASES).join('|')})\\b`,
  'i'
)

interface IrregularPlurals {
  [form: string]: string
}

// Forms whose plural is not the default `${form}s`.
const IRREGULAR_PLURALS: IrregularPlurals = {
  patch: 'patches',
  supp: 'supp',
}

export function formatMedicationDosageLine(
  request: ExtendedMedicationRequest
): string | null {
  const dose = formatDose(request)
  const qtyValue = request.dispenseRequest?.quantity?.value
  const hasQty = qtyValue != null && Number.isFinite(qtyValue)
  const form = hasQty ? formatForm(request, qtyValue) : null

  const parts: string[] = []
  if (dose) {
    parts.push(dose)
  }
  if (hasQty) {
    parts.push(
      form
        ? `Qty. ${formatQty(qtyValue)} ${form}`
        : `Qty. ${formatQty(qtyValue)}`
    )
  }

  return parts.length > 0 ? parts.join(', ') : null
}

export function formatMedicationInstructionLines(
  request: ExtendedMedicationRequest
): string[] {
  return (request.dosageInstructions ?? []).filter((line) => line.trim())
}

export function formatMedicationRefillLine(
  request: ExtendedMedicationRequest
): string | null {
  const authorized = request.refillsAuthorized
  const remaining = request.refillsRemaining
  if (authorized !== undefined && remaining !== undefined) {
    return `${remaining} of ${authorized} refills remaining`
  }

  const authorizedOnly = authorized ?? request.refills
  if (authorizedOnly === undefined) {
    return null
  }
  if (authorizedOnly === 0) {
    return 'No refills authorized'
  }
  return `${authorizedOnly} ${authorizedOnly === 1 ? 'refill' : 'refills'} authorized`
}

export function isMedicationRefillActionRequired(
  request: ExtendedMedicationRequest
): boolean {
  const authorized = request.refillsAuthorized
  const remaining = request.refillsRemaining
  if (authorized === undefined || authorized <= 0 || remaining === undefined) {
    return false
  }
  return remaining === 0
}

function formatDose(request: ExtendedMedicationRequest): string | null {
  const dosages = dosagesOf(request)
  const structured = dosages
    .flatMap((instruction) => instruction.doseAndRate ?? [])
    .map((rate) => rate.doseQuantity)
    .find(
      (quantity) => quantity?.value != null && isStrengthUnit(quantity.unit)
    )
  if (structured) {
    return joinDose(structured)
  }

  return (
    strengthFromText(request.medicationName) ??
    strengthFromText(request.medicationDisplay) ??
    dosages
      .map((instruction) => strengthFromText(instruction.text))
      .find((dose) => dose != null) ??
    null
  )
}

function joinDose(quantity: Quantity): string | null {
  if (quantity.value == null) {
    return null
  }
  const unit = quantity.unit?.replace(/\s+/g, '')
  return unit
    ? `${formatQty(quantity.value)}${unit}`
    : formatQty(quantity.value)
}

function isStrengthUnit(unit: string | undefined): boolean {
  return unit != null && STRUCTURED_STRENGTH_UNIT_PATTERN.test(unit.trim())
}

function strengthFromText(text: string | undefined): string | null {
  if (!text) {
    return null
  }
  const match = text.match(STRENGTH_PATTERN)
  if (!match) {
    return null
  }
  return `${formatQty(Number(match[1]))}${match[2].toLowerCase()}`
}

function formatForm(
  request: ExtendedMedicationRequest,
  qty: number
): string | null {
  const unit = request.dispenseRequest?.quantity?.unit
  const fromUnit = normalizeForm(unit)
  if (!fromUnit && unit && unit.toLowerCase() !== 'each') {
    return unit
  }

  const form =
    fromUnit ??
    dosagesOf(request)
      .map((instruction) => formFromText(instruction.text))
      .find((fromSig) => fromSig != null) ??
    formFromText(request.medicationName) ??
    formFromText(request.medicationDisplay)
  if (!form) {
    return null
  }
  return qty === 1 ? form : (IRREGULAR_PLURALS[form] ?? `${form}s`)
}

function normalizeForm(unit: string | undefined): string | null {
  if (!unit || unit.toLowerCase() === 'each') {
    return null
  }
  return FORM_ALIASES[unit.toLowerCase()] ?? null
}

function formFromText(text: string | undefined): string | null {
  if (!text) {
    return null
  }
  const match = text.match(FORM_PATTERN)
  return match ? (FORM_ALIASES[match[1].toLowerCase()] ?? null) : null
}

function formatQty(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(4)))
}

function dosagesOf(request: ExtendedMedicationRequest): Dosage[] {
  return request.toFhirResource().dosageInstruction ?? []
}
